// Path: frontend/src/lib/api/faceV2.ts
// Description: Face Auth API module - 人脸识别接口（含活体检测）

import { api } from './core';
import type { RecordingMetadata } from '../utils/videoRecording';

// ==========================================
// 📚 Type Definitions
// ==========================================

export type FaceCity = {
  id: number;
  name: string;
}

// 兼容旧引用（已弃用，新代码请用 FaceCity）
export type FaceSchool = FaceCity;

export type FaceStatus = {
  has_face: boolean;
  is_active: boolean;
  liveness_verified: boolean;
  quality_score: number | null;
  school_id: number | null;
  created_at: string | null;
  face_login_enabled: boolean;
  password_login_enabled: boolean;
}

export type FaceInfo = {
  user_id: number;
  username: string;
  has_face: boolean;
  quality_score: number | null;
  created_at: string | null;
  face_login_enabled?: boolean;
  password_login_enabled?: boolean;
}

export type FaceEnrollResponse = {
  success: boolean;
  message: string;
  quality_score: number;
  liveness_passed: boolean;
  face_id: number;
}

export type FaceLoginResponse = {
  access_token: string;
  token_type: string;
  user_id: number;
  username: string;
  role: string;
  school_id: number;
  avatar_url?: string;
  similarity_score: string | number;
  liveness_passed: boolean;
}

export type FaceVerifyResponse = {
  success: boolean;
  message: string;
  similarity_score: number;
}

export type FaceLog = {
  id: number;
  user_id: number;
  username: string;
  login_type: string;
  ip_address: string;
  device_info: string;
  created_at: string;
  similarity_score?: number;
  quality_score?: number;
  liveness_passed?: boolean;
}

export type AdminFaceResponse = {
  success: boolean;
  message: string;
  quality_score?: number;
  liveness_passed?: boolean;
}

export type ToggleLoginResponse = {
  success: boolean;
  message: string;
  face_login_enabled?: boolean;
  password_login_enabled?: boolean;
}

// ==========================================
// 🏙️ Cities API（人脸登录前选择城市）
// ==========================================

export const getCities = () =>
  api.get<FaceCity[]>('/face/cities/');

// 兼容旧引用（已弃用，新代码请用 getCities）
export const getSchools = getCities;

// ==========================================
// 🎭 User Face API Functions
// ==========================================

const FACE_TIMEOUT = 180000; // API请求超时：180秒（人脸识别需要较长时间）

/**
 * 用户首次录入人脸
 * @param formData 包含 video, image 和录制元数据的 FormData
 */
export const enrollFaceWithMetadata = (formData: FormData) => {
  return api.post<FaceEnrollResponse>('/face/enroll/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

/**
 * 用户首次录入人脸（简化版，兼容旧接口）
 * @deprecated 请使用 enrollFaceWithMetadata 传递完整元数据
 */
export const enrollFace = (video: File, image: File) => {
  const formData = new FormData();
  formData.append('video', video);
  formData.append('image', image);
  
  return api.post<FaceEnrollResponse>('/face/enroll/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

/**
 * 人脸登录
 * @param cityId 城市ID
 * @param formData 包含 video, image 和录制元数据的 FormData
 */
export const loginFaceWithMetadata = (cityId: number, formData: FormData) => {
  formData.append('city_id', cityId.toString());

  return api.post<FaceLoginResponse>('/face/login/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

/**
 * 人脸登录（简化版，兼容旧接口）
 * @deprecated 请使用 loginFaceWithMetadata 传递完整元数据
 */
export const loginFace = (cityId: number, video: File, image: File) => {
  const formData = new FormData();
  formData.append('city_id', cityId.toString());
  formData.append('video', video);
  formData.append('image', image);

  return api.post<FaceLoginResponse>('/face/login/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

export const getFaceInfo = () => 
  api.get<FaceInfo>('/face/info/');

export const getFaceStatus = () => 
  api.get<FaceStatus>('/face/status/');

export const verifyFace = (image: File) => {
  const formData = new FormData();
  formData.append('image', image);
  
  return api.post<FaceVerifyResponse>('/face/verify/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

export const getFaceLogs = (limit: number = 20) => 
  api.get<FaceLog[]>(`/face/logs/?limit=${limit}`);

// ==========================================
// 🔐 Admin Face API Functions
// ==========================================

/**
 * 管理员代录入人脸
 * @param userId 用户ID
 * @param formData 包含 video, image 和录制元数据的 FormData
 */
export const adminEnrollFaceWithMetadata = (userId: number, formData: FormData) => {
  return api.post<AdminFaceResponse>(`/face/admin/enroll/${userId}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

/**
 * 管理员代录入人脸（简化版，兼容旧接口）
 * @deprecated 请使用 adminEnrollFaceWithMetadata 传递完整元数据
 */
export const adminEnrollFace = (userId: number, video: File, image: File) => {
  const formData = new FormData();
  formData.append('video', video);
  formData.append('image', image);
  
  return api.post<AdminFaceResponse>(`/face/admin/enroll/${userId}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

/**
 * 管理员修改用户人脸
 * @param userId 用户ID
 * @param formData 包含 video, image 和录制元数据的 FormData
 */
export const adminUpdateFaceWithMetadata = (userId: number, formData: FormData) => {
  return api.put<AdminFaceResponse>(`/face/admin/update/${userId}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

/**
 * 管理员修改用户人脸（简化版，兼容旧接口）
 * @deprecated 请使用 adminUpdateFaceWithMetadata 传递完整元数据
 */
export const adminUpdateFace = (userId: number, video: File, image: File) => {
  const formData = new FormData();
  formData.append('video', video);
  formData.append('image', image);
  
  return api.put<AdminFaceResponse>(`/face/admin/update/${userId}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

export const adminDeleteFace = (userId: number) => 
  api.delete<AdminFaceResponse>(`/face/admin/delete/${userId}/`);

export const adminToggleFaceLogin = (userId: number, enabled: boolean) => {
  const formData = new FormData();
  formData.append('enabled', enabled.toString());
  
  return api.put<ToggleLoginResponse>(`/face/admin/toggle-login/${userId}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

export const adminTogglePasswordLogin = (userId: number, enabled: boolean) => {
  const formData = new FormData();
  formData.append('enabled', enabled.toString());
  
  return api.put<ToggleLoginResponse>(`/face/admin/toggle-password/${userId}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_TIMEOUT
  });
};

export const adminGetFaceLogs = (limit: number = 50, userId?: number) => {
  let url = `/face/admin/logs/?limit=${limit}`;
  if (userId) {
    url += `&user_id=${userId}`;
  }
  return api.get<FaceLog[]>(url);
};

// ==========================================
// 🚨 Error Handling
// ==========================================

export const FACE_ERRORS = {
  ALREADY_ENROLLED: '该用户已录入人脸',
  NOT_ENROLLED: '该用户未录入人脸',
  NO_FACE_DETECTED: '未检测到人脸，请确保面部清晰可见',
  MULTIPLE_FACES: '检测到多张人脸，请确保画面中只有一人',
  LOW_QUALITY: '人脸图像质量不佳，请重新录制',
  FACE_NOT_RECOGNIZED: '人脸识别失败，未找到匹配用户',
  SIMILARITY_LOW: '人脸相似度不足，请确保已录入本人人脸',
  USER_NOT_FOUND: '用户不存在或已被禁用',
  PERMISSION_DENIED: '权限不足',
  LIVENESS_FAILED: '活体检测失败，请确保是真人操作',
  SCHOOL_NOT_FOUND: '学校不存在',
  CITY_NOT_FOUND: '城市不存在',
  FACE_LOGIN_DISABLED: '刷脸登录未启用，请先录入人脸或联系管理员',
  PASSWORD_LOGIN_DISABLED: '密码登录已禁用，请使用刷脸登录',
  CANNOT_DISABLE_PASSWORD: '用户未启用刷脸登录，无法禁用密码登录',
  CANNOT_ENABLE_FACE: '用户未录入人脸，无法启用刷脸登录',
  NETWORK_ERROR: '网络连接失败，请检查网络',
  UNKNOWN_ERROR: '未知错误，请稍后重试',
};

export const parseFaceError = (error: any): string => {
  if (!error.response) {
    return FACE_ERRORS.NETWORK_ERROR;
  }
  
  const status = error.response?.status;
  const detail = error.response?.data?.detail || '';
  const message = error.response?.data?.message || '';
  const errorText = detail || message;
  
  // 403 错误特殊处理
  if (status === 403) {
    if (errorText.includes('face_login') || errorText.includes('刷脸') || errorText.includes('人脸登录')) {
      return FACE_ERRORS.FACE_LOGIN_DISABLED;
    }
    if (errorText.includes('password_login') || errorText.includes('密码登录')) {
      return FACE_ERRORS.PASSWORD_LOGIN_DISABLED;
    }
    return FACE_ERRORS.PERMISSION_DENIED;
  }
  
  if (errorText.includes('already') || errorText.includes('已录入')) {
    return FACE_ERRORS.ALREADY_ENROLLED;
  }
  if (errorText.includes('not enrolled') || errorText.includes('未录入')) {
    return FACE_ERRORS.NOT_ENROLLED;
  }
  if (errorText.includes('No face detected') || errorText.includes('未检测到人脸')) {
    return FACE_ERRORS.NO_FACE_DETECTED;
  }
  if (errorText.includes('Multiple faces') || errorText.includes('多张人脸')) {
    return FACE_ERRORS.MULTIPLE_FACES;
  }
  if (errorText.includes('quality') || errorText.includes('质量')) {
    return FACE_ERRORS.LOW_QUALITY;
  }
  if (errorText.includes('not recognized') || errorText.includes('未找到')) {
    return FACE_ERRORS.FACE_NOT_RECOGNIZED;
  }
  if (errorText.includes('similarity') || errorText.includes('相似度')) {
    return FACE_ERRORS.SIMILARITY_LOW;
  }
  if (errorText.includes('not found') || errorText.includes('不存在')) {
    return FACE_ERRORS.USER_NOT_FOUND;
  }
  if (errorText.includes('permission') || errorText.includes('权限')) {
    return FACE_ERRORS.PERMISSION_DENIED;
  }
  if (errorText.includes('liveness') || errorText.includes('活体')) {
    return FACE_ERRORS.LIVENESS_FAILED;
  }
  if (errorText.includes('city') || errorText.includes('城市')) {
    return FACE_ERRORS.CITY_NOT_FOUND;
  }
  if (errorText.includes('school') || errorText.includes('学校')) {
    return FACE_ERRORS.SCHOOL_NOT_FOUND;
  }
  if (errorText.includes('未启用刷脸') || errorText.includes('cannot disable password')) {
    return FACE_ERRORS.CANNOT_DISABLE_PASSWORD;
  }
  if (errorText.includes('未录入人脸') || errorText.includes('cannot enable face')) {
    return FACE_ERRORS.CANNOT_ENABLE_FACE;
  }
  
  return errorText || FACE_ERRORS.UNKNOWN_ERROR;
};
